import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import Header from "../../components/Header";
import ClientDashboard from "./ClientDashboard";
import DataEntry from "./DataEntry";
import Inventory from "./Inventory";
import Sales from "./Sales";
import Reports from "./Reports";
import Chat from "./Chat";
import Support from "./Support";
import {
  FiBarChart2,
  FiEdit3,
  FiArchive,
  FiDollarSign,
  FiFileText,
  FiMessageSquare,
  FiUser,
  FiHelpCircle,
} from "react-icons/fi";
import Profile from "../Profile";
import { useAuth } from "../../context/AuthContext";
import { reportsAPI } from "../../utils/reportsAPI";
import { chatAPI } from "../../utils/chatAPI";
import { useWebSocket } from "../../utils/useWebSocket";
import { useTranslation } from "react-i18next";
import "./ClientPortal.css";

const CLIENT_LINKS = (t) => [
  { path: "", label: t("nav.dashboard"), icon: FiBarChart2 },
  { path: "/data-entry", label: t("nav.dataEntry"), icon: FiEdit3 },
  { path: "/inventory", label: t("nav.inventory"), icon: FiArchive },
  {
    path: "/sales",
    label: t("nav.transactions", "Sales"),
    icon: FiDollarSign,
  },
  { path: "/reports", label: t("nav.reports"), icon: FiFileText },
  { path: "/chat", label: t("nav.chat"), icon: FiMessageSquare },
  { path: "/support", label: t("support.support"), icon: FiHelpCircle },
  { path: "/profile", label: t("profile.profile"), icon: FiUser },
];

function ClientPortal() {
  const { user } = useAuth();
  const location = useLocation();
  const [unseenReports, setUnseenReports] = useState(0);
  const [unreadChats, setUnreadChats] = useState(0);
  const { t } = useTranslation();

  const effectiveOrgId = useMemo(() => {
    if (user?.organization && typeof user.organization === "object")
      return user.organization.id;
    return user?.organization || null;
  }, [user]);

  const fetchUnseen = useCallback(async () => {
    if (!effectiveOrgId) return;
    try {
      const resp = await reportsAPI.unseenCount(effectiveOrgId);
      const count = resp?.data?.unseen_count ?? resp?.unseen_count ?? 0;
      setUnseenReports(count);
    } catch {
      // silent
    }
  }, [effectiveOrgId]);

  useEffect(() => {
    fetchUnseen();
  }, [fetchUnseen]);

  // Fetch unread chat count
  const fetchUnreadChats = useCallback(async () => {
    if (!effectiveOrgId) return;
    try {
      const resp = await chatAPI.getConversations(effectiveOrgId);
      const conversations = resp?.data || resp || [];
      const total = Array.isArray(conversations)
        ? conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0)
        : 0;
      setUnreadChats(total);
    } catch {
      // silent
    }
  }, [effectiveOrgId]);

  useEffect(() => {
    fetchUnreadChats();
    const interval = setInterval(fetchUnreadChats, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadChats]);

  // Clear badge when user navigates to reports page
  useEffect(() => {
    if (location.pathname.startsWith("/client/reports")) {
      setUnseenReports(0);
    }
    if (location.pathname.startsWith("/client/chat")) {
      setUnreadChats(0);
    }
  }, [location.pathname]);

  const onWsMessage = useCallback(
    (data) => {
      if (data?.type === "report_notification" && data?.event === "created") {
        if (!location.pathname.startsWith("/client/reports")) {
          setUnseenReports((prev) => prev + 1);
        }
      }
      if (data?.type === "chat_message" || data?.type === "new_message") {
        if (!location.pathname.startsWith("/client/chat")) {
          setUnreadChats((prev) => prev + 1);
        }
      }
    },
    [location.pathname],
  );
  useWebSocket(effectiveOrgId, onWsMessage);

  const sidebarBadges = useMemo(
    () => ({ "/reports": unseenReports, "/chat": unreadChats }),
    [unseenReports, unreadChats],
  );

  return (
    <div className="portal-layout">
      <Sidebar
        links={CLIENT_LINKS(t)}
        basePath="/client"
        badges={sidebarBadges}
      />

      <div className="portal-main">
        <Routes>
          <Route
            index
            element={
              <>
                <Header title={t("nav.dashboard")} />
                <ClientDashboard />
              </>
            }
          />
          <Route
            path="data-entry"
            element={
              <>
                <Header title={t("nav.dataEntry")} />
                <DataEntry />
              </>
            }
          />
          <Route
            path="inventory"
            element={
              <>
                <Header title={t("nav.inventory")} />
                <Inventory />
              </>
            }
          />
          <Route
            path="sales"
            element={
              <>
                <Header title={t("nav.transactions", "Sales")} />
                <Sales />
              </>
            }
          />
          <Route
            path="reports"
            element={
              <>
                <Header title={t("nav.reports")} />
                <Reports />
              </>
            }
          />
          <Route
            path="chat"
            element={
              <>
                <Header title={t("nav.chat")} />
                <Chat />
              </>
            }
          />
          <Route
            path="support"
            element={
              <>
                <Header title={t("support.support")} />
                <Support />
              </>
            }
          />
          <Route path="profile" element={<Profile />} />
          <Route path="*" element={<Navigate to="/client" />} />
        </Routes>
      </div>
    </div>
  );
}

export default ClientPortal;
